import type { AssessmentTemplate } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { toAssessmentTemplate } from './template-response'

interface AssessmentTemplateShowApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: (template: AssessmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `GET /admin-api/assessment-templates/{template_id}` — show (contract §2). The document is
 * normalised through `toAssessmentTemplate` (B7); nothing is validated here (B9).
 */
export const useAssessmentTemplateShowApi = async (
  options: AssessmentTemplateShowApiOptions,
): Promise<AssessmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assessment-templates.show', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template = toAssessmentTemplate(data.data)

    if (!template) {
      const msg = 'Unexpected error. The assessment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
